import React, { useState, useEffect, useMemo } from 'react';
import ReactQuill from 'react-quill-new';

import 'react-quill-new/dist/quill.snow.css';

import Select from 'react-select';
import api from '../utils/api';
import '../Admin.css';

const BlogManager = () => {
    const [posts, setPosts] = useState([]);
    const [offers, setOffers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        content: '',
        category: '',
        featuredImage: '',
        relatedOfferId: '',
        status: 'published'
    });

    useEffect(() => {
        fetchPosts();
        fetchOffers();
        fetchCategories();
    }, []);

    const fetchPosts = async () => {
        try {
            const response = await api.get('/blog');
            setPosts(response.data);
        } catch (error) {
            console.error('Erro ao buscar posts:', error);
        }
    };

    const fetchOffers = async () => {
        try {
            const response = await api.get('/ofertas');
            setOffers(response.data);
        } catch (error) {
            console.error('Erro ao buscar ofertas:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await api.get('/categorias');
            setCategories(response.data);
        } catch (error) {
            console.error('Erro ao buscar categorias:', error);
        }
    };

    const handleEdit = (post) => {
        setEditingPost(post);
        setFormData({
            title: post.title,
            slug: post.slug,
            content: post.content,
            category: post.category,
            featuredImage: post.featuredImage,
            relatedOfferId: post.relatedOfferId || '',
            status: post.status || 'published'
        });
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este post?')) {
            try {
                await api.delete(`/blog/${id}`);
                fetchPosts();
            } catch (error) {
                console.error('Erro ao deletar post:', error);
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (name === 'title' && !editingPost?.id) {
            const slug = value.toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
            setFormData(prev => ({ ...prev, slug }));
        }
    };

    const handleContentChange = (content) => {
        setFormData(prev => ({ ...prev, content }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (editingPost?.id) {
                await api.put(`/blog/${editingPost.id}`, formData);
            } else {
                await api.post('/blog', formData);
            }
            setEditingPost(null);
            setFormData({
                title: '',
                slug: '',
                content: '',
                category: '',
                featuredImage: '',
                relatedOfferId: '',
                status: 'published'
            });
            fetchPosts();
            alert('Post salvo com sucesso!');
        } catch (error) {
            console.error('Erro ao salvar post:', error);
            alert('Erro ao salvar post.');
        } finally {
            setLoading(false);
        }
    };

    const offerOptions = useMemo(() => {
        return offers.map(o => ({
            value: o.id,
            label: `${o.titulo} (${o.loja})`
        }));
    }, [offers]);

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image', 'video'],
            ['clean']
        ],
    };

    return (
        <div className="admin-container">
            <div className="page-header">
                <h2>Gerenciar Blog</h2>
                {!editingPost && (
                    <button className="btn-primary" onClick={() => setEditingPost({})}>
                        <i className="fas fa-plus"></i> Novo Post
                    </button>
                )}
            </div>

            {editingPost && (
                <div className="card-container" style={{ marginBottom: '30px' }}>
                    <div className="card-header">
                        <h3>{editingPost.id ? 'Editar Post' : 'Novo Post'}</h3>
                        <button className="btn-secondary" onClick={() => setEditingPost(null)}>Cancelar</button>
                    </div>
                    <form onSubmit={handleSubmit} className="blog-form" style={{ padding: '20px' }}>
                        <div className="form-group">
                            <label>Título *</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                placeholder="Título do artigo"
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Slug (URL amigável) *</label>
                            <input
                                type="text"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                                required
                                placeholder="ex: guia-de-compras-tvs"
                                className="form-input"
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div className="form-group">
                                <label>Categoria *</label>
                                <select 
                                    name="category" 
                                    value={formData.category} 
                                    onChange={handleChange} 
                                    required
                                    className="form-input"
                                >
                                    <option value="">Selecione...</option>
                                    {categories.map(c => (
                                        <option key={c.id} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Oferta Relacionada</label>
                                <Select
                                    options={offerOptions}
                                    value={offerOptions.find(o => o.value === formData.relatedOfferId)}
                                    onChange={(opt) => setFormData(prev => ({ ...prev, relatedOfferId: opt?.value || '' }))}
                                    isClearable
                                    placeholder="Buscar oferta..."
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '42px',
                                            borderColor: '#ddd'
                                        })
                                    }}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>URL da Imagem de Destaque *</label>
                            <input
                                type="url"
                                name="featuredImage"
                                value={formData.featuredImage}
                                onChange={handleChange}
                                required
                                placeholder="https://..."
                                className="form-input"
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: '80px' }}>
                            <label>Conteúdo *</label>
                            <ReactQuill
                                theme="snow"
                                value={formData.content}
                                onChange={handleContentChange}
                                modules={quillModules}
                                style={{ height: '350px' }}
                            />
                        </div>

                        <div className="form-actions" style={{ marginTop: '40px', display: 'flex', gap: '10px' }}>
                            <button type="button" className="btn-secondary" onClick={() => setEditingPost(null)}>
                                Cancelar
                            </button>
                            <button type="submit" className="btn-primary" disabled={loading}>
                                {loading ? 'Salvando...' : 'Salvar Post'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="card-container">
                <div className="card-header">
                    <h3>Posts Publicados</h3>
                </div>
                <div style={{ padding: '0 20px 20px 20px' }}>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Título</th>
                                <th>Categoria</th>
                                <th>Data</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {posts.length > 0 ? (
                                posts.map(post => (
                                    <tr key={post.id}>
                                        <td>{post.title}</td>
                                        <td>{post.category}</td>
                                        <td>{new Date(post.createdAt).toLocaleDateString('pt-BR')}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button className="btn-edit" onClick={() => handleEdit(post)} title="Editar">
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button className="btn-delete" onClick={() => handleDelete(post.id)} title="Excluir">
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                        Nenhum post encontrado.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default BlogManager;
